import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { WorkloadContextData, WorkloadData, WorkloadProviderProps } from './types';
import { TWO_WEEKLY_LIMIT, MONTHLY_LIMIT, initialState, getInitialDate } from './constants';
import { generateDays, getFirstDay, getLastDay } from './helpers';
import { useGetWorkloadCalendarSidebar } from '@services/workload/calendar/sidebar';
import { useGetWorkloadCalendar } from '@services/workload/calendar';
import { differenceInCalendarDays } from 'date-fns';
import { useGetWorkloadMonthlyCalendar } from '@services/workload/calendar/monthly';
import { useQueryParams } from '@hooks/queryParams';
import { useAppContext } from '@contexts/app';
import { toZonedTime } from 'date-fns-tz';

const WorkloadContext = createContext<WorkloadContextData | Record<string, never>>({});

export const WorkloadProvider = ({ children }: WorkloadProviderProps) => {
  const { tenant } = useAppContext();
  const [state, setState] = useState<WorkloadContextData>(initialState);
  const { visibleDays, date } = state;

  const {
    params: { type },
  } = useQueryParams();
  const isTwoWeekly = type === 'two-week';

  const first = getFirstDay(date, type);
  const last = getLastDay(date, type);
  const difference = differenceInCalendarDays(last, first);

  const calendar = useGetWorkloadCalendar(date);
  const monthly = useGetWorkloadMonthlyCalendar(first);
  const sidebar = useGetWorkloadCalendarSidebar();

  const setData = useCallback((prop: keyof WorkloadData, value: unknown): void => {
    setState((prev) => ({ ...prev, [prop]: value }));
  }, []);

  useEffect(() => {
    if (!tenant.zone) return;
    setData('date', getInitialDate(toZonedTime(new Date(), tenant.zone)));
  }, [tenant.zone, setData]);

  useEffect(() => {
    if (!visibleDays) return;
    setData('dates', generateDays(visibleDays, date));
  }, [date, visibleDays, setData]);

  return (
    <WorkloadContext.Provider
      value={{
        ...state,
        calendar: { isLoading: calendar.isLoading, records: calendar.data?.records || [] },
        sidebar: { isLoading: sidebar.isLoading, records: sidebar.data?.records || [] },
        setData,
        isTwoWeekly,
        monthly: {
          ...state.monthly,
          records: monthly.data?.records || {},
          isLoading: monthly.isLoading,
          limit: isTwoWeekly ? TWO_WEEKLY_LIMIT : MONTHLY_LIMIT,
          first,
          last,
          dates: generateDays(difference - (difference % state.monthly.weekLength), first),
        },
      }}>
      {children}
    </WorkloadContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useWorkloadContext = () => useContext(WorkloadContext);
